import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from '../../context/AuthContext.js';
import HourValue from './HourValue';

const HOUR_DAYS = [['monday','Ma'],['tuesday','Di'],['wednesday','Wo'],['thursday','Do'],['friday','Vr'],['saturday','Za'],['sunday','Zo']];
const validHourValue = value => value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 24 && Math.abs(Number(value)*100-Math.round(Number(value)*100)) < 0.000001;

const AdminHoursManagement = () => {
    const [weeks, setWeeks] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState("");
    const [selectedUser, setSelectedUser] = useState("all");
    const [hoursData, setHoursData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState("ingediend");
    const [showDetails, setShowDetails] = useState(false);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
    });

    const token = sessionStorage.getItem('jwtToken');
    const { userInfo } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [editedHours, setEditedHours] = useState({});
    const [savingDetails, setSavingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [actionMessage, setActionMessage] = useState('');
    const canEditDetails = ['super_admin', 'locatie_admin'].includes(userInfo?.role) && selectedDetails?.canEdit;
    const hasHourChanges = isEditing && (selectedDetails?.tasks || []).some(task =>
        HOUR_DAYS.some(([day]) => Number(editedHours[task.user_hour_id]?.[day]) !== Number(task[day] || 0)));
    const hasInvalidHours = isEditing && Object.values(editedHours).some(row => HOUR_DAYS.some(([day]) => !validHourValue(row[day])));
    const detailTaskTotal = task => HOUR_DAYS.reduce((sum,[day]) => sum + Number((isEditing ? editedHours[task.user_hour_id]?.[day] : task[day]) || 0), 0);
    const detailWeekTotal = (selectedDetails?.tasks || []).reduce((sum,task) => sum + detailTaskTotal(task), 0);
    const closeDetails = () => {
        if (savingDetails) return;
        if (hasHourChanges && !window.confirm('Niet-opgeslagen wijzigingen sluiten?')) return;
        setShowDetails(false); setIsEditing(false); setEditedHours({}); setDetailsError('');
    };
    const beginEditing = () => {
        if (!canEditDetails || savingDetails) return;
        setEditedHours(Object.fromEntries(selectedDetails.tasks.map(task => [task.user_hour_id,
            Object.fromEntries(HOUR_DAYS.map(([day]) => [day, String(task[day] ?? 0)]))])));
        setDetailsError(''); setIsEditing(true);
    };
    const cancelEditing = () => { setIsEditing(false); setEditedHours({}); setDetailsError(''); };
    const renderHourValue = (task, day) => {
        if (!isEditing) return task[day];
        return <input type="number" className="form-control form-control-sm text-center" min="0" max="24" step="0.01" required
            style={{ paddingLeft: '0.125rem', paddingRight: '0.125rem' }}
            aria-label={task.task_name + ' ' + HOUR_DAYS.find(([key]) => key === day)[1]}
            aria-invalid={!validHourValue(editedHours[task.user_hour_id]?.[day])}
            value={editedHours[task.user_hour_id]?.[day] ?? ''} disabled={savingDetails}
            onChange={event => { const value = event.target.value; setEditedHours(previous => ({...previous,
                [task.user_hour_id]: {...previous[task.user_hour_id], [day]: value}})); }} />;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [weeksRes, usersRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/weeks`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/users`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                setWeeks(weeksRes.data.data || []);
                setUsers(usersRes.data.data || []);
            } catch (error) {
                console.error('Fout bij ophalen data:', error);
            }
        };

        fetchData();
    }, [token]);

    const fetchHours = useCallback(async () => {
        if (!selectedWeek) {
            setHoursData([]);
            setPagination({
                page: 1,
                limit,
                total: 0,
                totalPages: 1,
                hasNextPage: false,
                hasPrevPage: false
            });
            return;
        }

        setLoading(true);

        try {
            const params = {
                status: filterStatus,
                user_id: selectedUser !== 'all' ? selectedUser : undefined,
                page,
                limit
            };

            let response;

            if (selectedWeek === "all") {
                response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/all-hours`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params
                    }
                );
            } else {
                const [year, week] = selectedWeek.split('-');

                response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/hours`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { ...params, year, week }
                    }
                );
            }

            setHoursData(response.data.data || []);
            setPagination(
                response.data.pagination || {
                    page: 1,
                    limit,
                    total: 0,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPrevPage: false
                }
            );
        } catch (error) {
            console.error('Fout bij ophalen uren:', error);
            setHoursData([]);
            setPagination({
                page: 1,
                limit,
                total: 0,
                totalPages: 1,
                hasNextPage: false,
                hasPrevPage: false
            });
        } finally {
            setLoading(false);
        }
    }, [selectedWeek, selectedUser, filterStatus, token, page, limit]);

    useEffect(() => {
        fetchHours();
    }, [fetchHours]);

    const fetchHoursDetails = async (userId, weekYear, weekNumber) => {
        setDetailsLoading(true);
        setIsEditing(false); setEditedHours({}); setDetailsError('');

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/hours-details`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { user_id: userId, year: weekYear, week: weekNumber }
                }
            );

            setSelectedDetails({
                user: { ...hoursData.find(
                    (item) => item.user_id === userId && item.week_year === weekYear && item.week_number === weekNumber
                ), status: response.data.week?.status || hoursData.find(item => item.user_id === userId && item.week_year === weekYear && item.week_number === weekNumber)?.status },
                tasks: response.data.data || [],
                canEdit: response.data.can_edit === true,
                revision: response.data.revision
            });

            setShowDetails(true);
        } catch (error) {
            console.error('Fout bij ophalen details:', error);
        } finally {
            setDetailsLoading(false);
        }
    };

    const saveAndApproveDetails = async () => {
        if (!canEditDetails || !hasHourChanges || hasInvalidHours || savingDetails) return;
        setSavingDetails(true); setDetailsError(''); setActionMessage('');
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/update-and-approve-hours`,
                { user_id: selectedDetails.user.user_id, week_year: selectedDetails.user.week_year, week_number: selectedDetails.user.week_number,
                  revision: selectedDetails.revision, hours: selectedDetails.tasks.map(task => ({ user_hour_id: task.user_hour_id,
                      ...Object.fromEntries(HOUR_DAYS.map(([day]) => [day, Number(editedHours[task.user_hour_id][day])])) })) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowDetails(false); setIsEditing(false); setEditedHours({});
            setActionMessage('Uren gewijzigd en goedgekeurd.');
            await fetchHours();
        } catch (error) {
            setDetailsError(error.response?.data?.error || error.response?.data?.message || 'Opslaan en goedkeuren mislukt. Probeer opnieuw.');
        } finally { setSavingDetails(false); }
    };

    const handleApprove = async (userId, weekYear, weekNumber) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/approve-hours`,
                {
                    user_id: userId,
                    week_year: weekYear,
                    week_number: weekNumber
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            fetchHours();
        } catch (error) {
            console.error('Fout bij goedkeuren:', error);
        }
    };

    const handleReject = async (userId, weekYear, weekNumber) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/user-hours/admin/reject-hours`,
                {
                    user_id: userId,
                    week_year: weekYear,
                    week_number: weekNumber,
                    reason: "Afgekeurd door admin"
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            fetchHours();
        } catch (error) {
            console.error('Fout bij afkeuren:', error);
        }
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    const renderPaginationButtons = () => {
        if ((pagination.totalPages || 1) <= 1) return null;

        const currentPage = pagination.page || 1;
        const totalPages = pagination.totalPages || 1;

        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, currentPage + 2);

        if (currentPage <= 2) {
            endPage = Math.min(totalPages, 5);
        }

        if (currentPage >= totalPages - 1) {
            startPage = Math.max(1, totalPages - 4);
        }

        const buttons = [];

        for (let i = startPage; i <= endPage; i += 1) {
            buttons.push(
                <button
                    key={i}
                    type="button"
                    className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setPage(i)}
                >
                    {i}
                </button>
            );
        }

        return buttons;
    };

    const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
    const endRecord =
        pagination.total === 0
            ? 0
            : Math.min(pagination.page * pagination.limit, pagination.total);

    const totalHoursCurrentPage = hoursData
        .reduce((sum, item) => sum + parseFloat(item.total_hours || 0), 0);

    const MobileHoursCard = ({ userWeek }) => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div className="card mb-3">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                            <h6 className="mb-1">{userWeek.user_name}</h6>
                            <small className="text-muted">{userWeek.user_email}</small>
                            <div className="mt-1">
                                <span
                                    className={`badge ${
                                        userWeek.status === 'goedgekeurd'
                                            ? 'bg-success'
                                            : userWeek.status === 'ingediend'
                                            ? 'bg-warning'
                                            : 'bg-secondary'
                                    }`}
                                >
                                    {userWeek.status}
                                </span>
                            </div>
                        </div>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? "↑" : "↓"}
                        </button>
                    </div>
                </div>

                {expanded && (
                    <div className="card-body">
                        <div className="row">
                            <div className="col-6">
                                <strong>Week:</strong><br />
                                {userWeek.week_number} - {userWeek.week_year}
                            </div>
                            <div className="col-6">
                                <strong>Totaal:</strong><br />
                                <HourValue value={userWeek.total_hours} /> uur
                            </div>
                        </div>

                        <div className="mt-3 d-flex flex-wrap gap-2">
                            <button
                                className="btn btn-outline-info btn-sm flex-fill"
                                onClick={() =>
                                    fetchHoursDetails(
                                        userWeek.user_id,
                                        userWeek.week_year,
                                        userWeek.week_number
                                    )
                                }
                            >
                                Details
                            </button>

                            {userWeek.status === 'ingediend' && (
                                <>
                                    <button
                                        className="btn btn-success btn-sm flex-fill"
                                        onClick={() =>
                                            handleApprove(
                                                userWeek.user_id,
                                                userWeek.week_year,
                                                userWeek.week_number
                                            )
                                        }
                                    >
                                        ✓ Goedkeuren
                                    </button>
                                    <button
                                        className="btn btn-danger btn-sm flex-fill"
                                        onClick={() =>
                                            handleReject(
                                                userWeek.user_id,
                                                userWeek.week_year,
                                                userWeek.week_number
                                            )
                                        }
                                    >
                                        ✗ Afkeuren
                                    </button>
                                </>
                            )}

                            {userWeek.status === 'goedgekeurd' && (
                                <button
                                    className="btn btn-warning btn-sm flex-fill"
                                    onClick={() =>
                                        handleReject(
                                            userWeek.user_id,
                                            userWeek.week_year,
                                            userWeek.week_number
                                        )
                                    }
                                >
                                    Terug naar concept
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="container-fluid mt-3">
            <h2 className="mb-3">Urenbeheer</h2>

            <div className="mb-3">
                <a href="/hourreports" className="btn btn-primary">
                    Ga naar Uren Rapportages
                </a>
            </div>

            <div className="row mb-3">
                <div className="col-12 col-md-6 col-lg-3 mb-2">
                    <label className="form-label">Week</label>
                    <select
                        className="form-select"
                        value={selectedWeek}
                        onChange={(e) => {
                            setSelectedWeek(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">Selecteer week</option>
                        <option value="all">📅 Alle weken</option>
                        {weeks.map((week) => (
                            <option
                                key={`${week.week_year}-${week.week_number}`}
                                value={`${week.week_year}-${week.week_number}`}
                            >
                                Week {week.week_number} - {week.week_year}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-12 col-md-6 col-lg-3 mb-2">
                    <label className="form-label">Medewerker</label>
                    <select
                        className="form-select"
                        value={selectedUser}
                        onChange={(e) => {
                            setSelectedUser(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="all">👥 Alle medewerkers</option>
                        {users.map((user) => (
                            <option key={user.user_id} value={user.user_id}>
                                {user.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-12 col-md-6 col-lg-2 mb-2">
                    <label className="form-label">Status</label>
                    <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="ingediend">Ingediend</option>
                        <option value="goedgekeurd">Goedgekeurd</option>
                        <option value="draft">Concept</option>
                        <option value="all">Alle statussen</option>
                    </select>
                </div>

                <div className="col-12 col-md-6 col-lg-2 mb-2">
                    <label className="form-label">Aantal</label>
                    <select
                        className="form-select"
                        value={limit}
                        onChange={handleLimitChange}
                    >
                        <option value={10}>Toon 10</option>
                        <option value={20}>Toon 20</option>
                        <option value={50}>Toon 50</option>
                        <option value={100}>Toon 100</option>
                    </select>
                </div>

                <div className="col-12 col-lg-2 mb-2">
                    <label className="form-label">&nbsp;</label>
                    <div className="text-muted small">
                        {selectedWeek === "all" ? (
                            <span>Alle weken</span>
                        ) : selectedWeek ? (
                            <span>Week {selectedWeek.split('-')[1]} - {selectedWeek.split('-')[0]}</span>
                        ) : (
                            <span>Selecteer een week</span>
                        )}
                        <br />
                        {selectedUser === "all" ? (
                            <span>Alle medewerkers</span>
                        ) : (
                            <span>1 medewerker</span>
                        )}
                    </div>
                </div>
            </div>

            {selectedWeek && (
                <div className="row mb-3 align-items-center">
                    <div className="col-12 col-md-6 mb-2 mb-md-0">
                        <small className="text-muted">
                            Toont {startRecord} t/m {endRecord} van {pagination.total || 0} resultaten
                        </small>
                    </div>
                    <div className="col-12 col-md-6 text-md-end">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                setSelectedWeek("");
                                setSelectedUser("all");
                                setFilterStatus("ingediend");
                                setLimit(20);
                                setPage(1);
                                setHoursData([]);
                                setPagination({
                                    page: 1,
                                    limit: 20,
                                    total: 0,
                                    totalPages: 1,
                                    hasNextPage: false,
                                    hasPrevPage: false
                                });
                            }}
                        >
                            Filters wissen
                        </button>
                    </div>
                </div>
            )}

            {hoursData.length > 0 && (
                <div className="alert alert-info mb-3">
                    <strong>{pagination.total || hoursData.length}</strong> resultaten • Deze pagina:{' '}
                    <strong><HourValue value={totalHoursCurrentPage} /></strong> uur
                </div>
            )}

            <div className="d-block d-lg-none">
                {loading ? (
                    <div className="text-center py-4">Laden...</div>
                ) : hoursData.length > 0 ? (
                    hoursData.map((userWeek) => (
                        <MobileHoursCard
                            key={`${userWeek.user_id}-${userWeek.week_year}-${userWeek.week_number}`}
                            userWeek={userWeek}
                        />
                    ))
                ) : selectedWeek ? (
                    <div className="text-center text-muted py-4">
                        Geen uren gevonden voor de geselecteerde filters
                    </div>
                ) : null}
            </div>

            {actionMessage && <div className="alert alert-success" role="status">{actionMessage}</div>}
            <div className="d-none d-lg-block">
                {loading ? (
                    <div className="text-center py-4">Laden...</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Medewerker</th>
                                    <th>Week</th>
                                    <th>Totaal uren</th>
                                    <th>Status</th>
                                    <th>Acties</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hoursData.map((userWeek) => (
                                    <tr key={`${userWeek.user_id}-${userWeek.week_year}-${userWeek.week_number}`}>
                                        <td>
                                            <strong>{userWeek.user_name}</strong>
                                            <br />
                                            <small className="text-muted">{userWeek.user_email}</small>
                                        </td>
                                        <td>
                                            Week {userWeek.week_number} - {userWeek.week_year}
                                            <br />
                                            <small className="text-muted">
                                                {new Date(userWeek.submitted_at).toLocaleDateString('nl-NL')}
                                            </small>
                                        </td>
                                        <td>
                                            <strong><HourValue value={userWeek.total_hours} /></strong> uur
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${
                                                    userWeek.status === 'goedgekeurd'
                                                        ? 'bg-success'
                                                        : userWeek.status === 'ingediend'
                                                        ? 'bg-warning'
                                                        : 'bg-secondary'
                                                }`}
                                            >
                                                {userWeek.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1 flex-wrap">
                                                <button
                                                    className="btn btn-outline-info btn-sm"
                                                    onClick={() =>
                                                        fetchHoursDetails(
                                                            userWeek.user_id,
                                                            userWeek.week_year,
                                                            userWeek.week_number
                                                        )
                                                    }
                                                >
                                                    Details
                                                </button>

                                                {userWeek.status === 'ingediend' && (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() =>
                                                                handleApprove(
                                                                    userWeek.user_id,
                                                                    userWeek.week_year,
                                                                    userWeek.week_number
                                                                )
                                                            }
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() =>
                                                                handleReject(
                                                                    userWeek.user_id,
                                                                    userWeek.week_year,
                                                                    userWeek.week_number
                                                                )
                                                            }
                                                        >
                                                            ✗
                                                        </button>
                                                    </>
                                                )}

                                                {userWeek.status === 'goedgekeurd' && (
                                                    <button
                                                        className="btn btn-warning btn-sm"
                                                        onClick={() =>
                                                            handleReject(
                                                                userWeek.user_id,
                                                                userWeek.week_year,
                                                                userWeek.week_number
                                                            )
                                                        }
                                                    >
                                                        Terug
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {hoursData.length === 0 && selectedWeek && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted">
                                            Geen uren gevonden voor de geselecteerde filters
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedWeek && (pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={!pagination.hasPrevPage}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        >
                            Vorige
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={!pagination.hasNextPage}
                            onClick={() => setPage((prev) => prev + 1)}
                        >
                            Volgende
                        </button>
                    </div>

                    <div className="d-flex flex-wrap justify-content-center gap-2">
                        {renderPaginationButtons()}
                    </div>
                </div>
            )}

            {showDetails && selectedDetails && (
                <div className="modal show d-block" role="dialog" aria-modal="true" aria-label="Uren details" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Uren details - {selectedDetails.user.user_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Sluit urendetails" disabled={savingDetails}
                                    onClick={closeDetails}
                                ></button>
                            </div>

                            <div className="modal-body">
                                {detailsError && <div className="alert alert-danger" role="alert">{detailsError}</div>}
                                {isEditing && <p className="alert alert-info">Wijzig de uren. Opslaan keurt de volledige week direct goed.</p>}
                                {hasInvalidHours && <div className="alert alert-warning" role="alert">Vul per veld een getal van 0 tot en met 24 in, met maximaal twee decimalen.</div>}
                                {detailsLoading ? (
                                    <div className="text-center">Details laden...</div>
                                ) : (
                                    <>
                                        <div className="row mb-3">
                                            <div className="col-12 col-md-6">
                                                <strong>Week:</strong> {selectedDetails.user.week_number} - {selectedDetails.user.week_year}
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <strong>Status:</strong>
                                                <span
                                                    className={`badge ms-2 ${
                                                        selectedDetails.user.status === 'goedgekeurd'
                                                            ? 'bg-success'
                                                            : selectedDetails.user.status === 'ingediend'
                                                            ? 'bg-warning'
                                                            : 'bg-secondary'
                                                    }`}
                                                >
                                                    {selectedDetails.user.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="d-none d-md-block">
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Taak</th>
                                                            <th>Ma</th>
                                                            <th>Di</th>
                                                            <th>Wo</th>
                                                            <th>Do</th>
                                                            <th>Vr</th>
                                                            <th>Za</th>
                                                            <th>Zo</th>
                                                            <th>Totaal</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedDetails.tasks.map((task, index) => (
                                                            <tr key={index}>
                                                                <td>{task.task_name}</td>
                                                                <td>{renderHourValue(task, 'monday')}</td>
                                                                <td>{renderHourValue(task, 'tuesday')}</td>
                                                                <td>{renderHourValue(task, 'wednesday')}</td>
                                                                <td>{renderHourValue(task, 'thursday')}</td>
                                                                <td>{renderHourValue(task, 'friday')}</td>
                                                                <td>{renderHourValue(task, 'saturday')}</td>
                                                                <td>{renderHourValue(task, 'sunday')}</td>
                                                                <td>
                                                                    <strong><HourValue value={detailTaskTotal(task)} /></strong>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="table-light">
                                                            <td colSpan="8" className="text-end">
                                                                <strong>Totaal week:</strong>
                                                            </td>
                                                            <td>
                                                                <strong><HourValue value={detailWeekTotal} /></strong>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>

                                        <div className="d-block d-md-none">
                                            {selectedDetails.tasks.map((task, index) => (
                                                <div key={index} className="card mb-3">
                                                    <div className="card-header bg-light">
                                                        <h6 className="mb-0">{task.task_name}</h6>
                                                        <small className="text-muted">
                                                            Totaal: <HourValue value={detailTaskTotal(task)} /> uur
                                                        </small>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row g-2 text-center">
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Ma</div><div className="fw-bold">{renderHourValue(task, 'monday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Di</div><div className="fw-bold">{renderHourValue(task, 'tuesday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Wo</div><div className="fw-bold">{renderHourValue(task, 'wednesday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Do</div><div className="fw-bold">{renderHourValue(task, 'thursday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Vr</div><div className="fw-bold">{renderHourValue(task, 'friday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Za</div><div className="fw-bold">{renderHourValue(task, 'saturday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-white"><div className="small text-muted">Zo</div><div className="fw-bold">{renderHourValue(task, 'sunday')}</div></div></div>
                                                            <div className="col-3"><div className="border rounded p-2 bg-primary text-white"><div className="small">Totaal</div><div className="fw-bold"><HourValue value={detailTaskTotal(task)} /></div></div></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <strong>Week totaal: <HourValue value={detailWeekTotal} /> uur</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary" disabled={savingDetails}
                                    onClick={closeDetails}
                                >
                                    Sluiten
                                </button>
                                {canEditDetails && !isEditing && <button type="button" className="btn btn-outline-primary" onClick={beginEditing}>Wijzig invoer</button>}
                                {isEditing && <button type="button" className="btn btn-outline-secondary" disabled={savingDetails} onClick={cancelEditing}>Annuleer wijzigingen</button>}
                                {isEditing && hasHourChanges && <button type="button" className="btn btn-success" disabled={savingDetails || hasInvalidHours} onClick={saveAndApproveDetails}>
                                    {savingDetails ? 'Bezig met opslaan...' : 'Opslaan en goedkeuren'}
                                </button>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminHoursManagement;
